import React from 'react';
import classNames from 'classnames';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import {
    RadioGroup,
    TextInput,
} from '@instinet-analytics/semantic-ui-components';
import { useTilesConfigSlice } from 'widgets/tiles/store/config/configSlice';
import {
    TilesOrientation,
    CapsuleContainerWidgetConfigState,
} from 'widgets/tiles/store/config/CapsuleContainerWidgetConfigState';
import './TilesSettings.scss';
import AppSettingsEditor from 'appToolbar/AppSettingsEditor';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useDispatchUpdateAppSettings } from 'app/store/appSettings/dispatcher';
import WidgetSettingsSymbolEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsSymbolEditor';
import { useWidgetConfigUpdater } from 'widgets/WidgetSettingsEditing/useWidgetConfigUpdater';

export interface TilesSettingsProps {
    config?: CapsuleContainerWidgetConfigState;
    onChange?: (nextDelta: Partial<CapsuleContainerWidgetConfigState>) => void;
}

export default function TilesSettings(props: TilesSettingsProps) {
    const { onChange, config: propsConfig } = props;

    const isAppModeNewportSingleWidget =
        useSelectIsAppModeNewportSingleWidget();
    const appSettings = useSelectAppSettings();
    const dispatchUpdateAppSettings = useDispatchUpdateAppSettings();

    const { config: configSliceConfig, dispatchConfigChanged } =
        useTilesConfigSlice();

    const config = propsConfig ?? configSliceConfig;

    const {
        debouncedApplyWidgetConfigChange,
        immediateApplyWidgetConfigChange,
    } = useWidgetConfigUpdater(dispatchConfigChanged, onChange);

    const { orientation, tabTitle } = config;

    const onOrientationChanged = (nextOrientation: TilesOrientation) => {
        immediateApplyWidgetConfigChange({ orientation: nextOrientation });
    };

    const onTitleChanged = (nextTitle: string) => {
        immediateApplyWidgetConfigChange({ tabTitle: nextTitle });
    };

    return (
        <div
            className={classNames(
                'TilesSettings',
                isAppModeNewportSingleWidget && 'single-widget'
            )}
        >
            <div className="group">
                <WidgetSettingsSymbolEditor
                    config={config}
                    onChange={debouncedApplyWidgetConfigChange}
                />
                <div>Orientation</div>
                <RadioGroup
                    selected={orientation}
                    onChange={onOrientationChanged}
                    className="orientation-toggle"
                    options={[
                        {
                            key: 'row',
                            label: 'Rows',
                        },
                        {
                            key: 'column',
                            label: 'Columns',
                        },
                    ]}
                />
            </div>
            {!isAppModeNewportSingleWidget && (
                <div className="group">
                    <div>Tab Title</div>
                    <TextInput
                        className="edit-title-input"
                        value={tabTitle}
                        onChange={onTitleChanged}
                    />
                    <div className="help">
                        <span className="loud">{'{symbol}'}</span> and{' '}
                        <span className="loud">{'{symbolName}'}</span> will be
                        replaced using the selected or configured symbol
                    </div>
                </div>
            )}
            {isAppModeNewportSingleWidget && (
                <AppSettingsEditor
                    appSettings={appSettings}
                    onAppSettingsChanged={dispatchUpdateAppSettings}
                    specifyVisibleControls={{
                        symbology: true,
                        showChartHelp: true,
                        showTooltips: true,
                    }}
                />
            )}
        </div>
    );
}
